import { cloneDeep } from 'lodash'

/**
 * The id keys a select-component filter can carry, in the order they're
 * preferred when a value somehow carries more than one.
 */
const selectComponentIdKeys = ['campaign_id', 'ad_group_id', 'placement_id', 'portfolio_id']

const selectedId = (value: Record<string, any>): unknown =>
    selectComponentIdKeys
        .map((idKey) => value[idKey])
        .find((id) => id !== undefined && id !== null && id !== '')

/**
 * Drops select-component filters that don't have an id selected yet.
 *
 * A selector renders as `{ type: 'select-component' }` before anything has been
 * picked. Sending that shape to the API filters on nothing, so strip it out
 * rather than let it reach the backend.
 *
 * @param filters - The filters object to sanitize
 * @returns A new filters object with incomplete select-component entries removed
 */
export const removeIncompleteSelectComponentFilters = <T extends Record<string, any>>(
    filters: T | undefined,
): T | undefined => {
    if (!filters) {
        return filters
    }

    const filtersCopy = cloneDeep(filters)

    Object.keys(filtersCopy).forEach((key) => {
        const value = filtersCopy[key]

        if (value?.type === 'select-component' && selectedId(value) === undefined) {
            delete filtersCopy[key]
        }
    })

    return filtersCopy
}

/**
 * Transforms select-component filters from their full object form to just the ID value.
 *
 * Select component filters store objects like:
 * { campaign_id: 123, name: 'Campaign Name', type: 'select-component' }
 *
 * This function extracts just the ID (e.g., 123) for use in API requests.
 * Entries without an id selected are dropped rather than transformed.
 *
 * @param params - The params object containing filters
 * @returns A new params object with transformed filters
 */
export const transformSelectComponentFilters = <T extends { filter?: Record<string, any> }>(
    params: T,
): T => {
    const paramsCopy = cloneDeep(params)

    if (paramsCopy.filter) {
        paramsCopy.filter = removeIncompleteSelectComponentFilters(paramsCopy.filter) as Record<
            string,
            any
        >

        Object.keys(paramsCopy.filter).forEach((k) => {
            const value = paramsCopy.filter[k]

            if (value?.type === 'select-component') {
                paramsCopy.filter[k] = selectedId(value)
            }
        })
    }

    return paramsCopy
}
